import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setOnLoginCallback } from './AuthContext';

const CartContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CartProvider = ({ children }) => {
  const [cartId, setCartId] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Initialize cart from localStorage or create new
  useEffect(() => {
    const storedCartId = localStorage.getItem('cartId');
    if (storedCartId) {
      setCartId(storedCartId);
      fetchCart(storedCartId);
    }
  }, []);

  const fetchCart = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/cart/${id}`);
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      } else {
        // Cart not found, clear localStorage
        localStorage.removeItem('cartId');
        setCartId(null);
        setCart(null);
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  };

  const createCart = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cart/create`, {
        method: 'POST'
      });
      const data = await response.json();
      localStorage.setItem('cartId', data.cart_id);
      setCartId(data.cart_id);
      return data.cart_id;
    } catch (err) {
      console.error('Error creating cart:', err);
      throw err;
    }
  };

  const addToCart = useCallback(async (item) => {
    setLoading(true);
    setError(null);
    
    try {
      let currentCartId = cartId;
      if (!currentCartId) {
        currentCartId = await createCart();
      }

      const response = await fetch(`${API_URL}/api/cart/${currentCartId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const updatedCart = await response.json();
      setCart(updatedCart);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  const removeFromCart = useCallback(async (itemIndex) => {
    if (!cartId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cart/${cartId}/item/${itemIndex}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const updatedCart = await response.json();
        setCart(updatedCart);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  const updateQuantity = useCallback(async (itemIndex, quantity) => {
    if (!cartId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/cart/${cartId}/item/${itemIndex}/quantity?quantity=${quantity}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        const updatedCart = await response.json();
        setCart(updatedCart);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  const getShippingEstimates = useCallback(async (address) => {
    if (!cartId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/shipping/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_id: cartId,
          address
        })
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Error getting shipping estimates:', err);
      return null;
    }
  }, [cartId]);

  const selectShipping = useCallback(async (serviceCode, price) => {
    if (!cartId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/shipping/select?cart_id=${cartId}&service_code=${serviceCode}&shipping_price=${price}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const updatedCart = await response.json();
        setCart(updatedCart);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error selecting shipping:', err);
      return false;
    }
  }, [cartId]);

  const createCheckoutSession = useCallback(async (shippingAddress, shippingService, termsConsentData = null) => {
    if (!cartId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_id: cartId,
          shipping_address: shippingAddress,
          shipping_service: shippingService,
          origin_url: window.location.origin,
          terms_consent: termsConsentData
        })
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      return null;
    }
  }, [cartId]);

  // Customer cash checkout: registers the order without going to Stripe.
  // paymentSubtype must be "pickup" (in-store) or "cod" (cash on delivery).
  const createCashCheckout = useCallback(async (shippingAddress, shippingService, paymentSubtype, customerInfo, termsConsentData = null) => {
    if (!cartId) return { success: false, error: 'No active cart' };
    try {
      const response = await fetch(`${API_URL}/api/checkout/cash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_id: cartId,
          shipping_address: shippingAddress,
          shipping_service: shippingService,
          payment_subtype: paymentSubtype,
          customer_name: customerInfo?.name || shippingAddress?.name || '',
          customer_email: customerInfo?.email || null,
          customer_phone: customerInfo?.phone || shippingAddress?.phone || null,
          notes: customerInfo?.notes || null,
          terms_consent: termsConsentData,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, ...data };
      }
      const errorBody = await response.json().catch(() => ({}));
      return { success: false, error: errorBody.detail || 'Cash checkout failed' };
    } catch (err) {
      console.error('Error creating cash checkout:', err);
      return { success: false, error: 'Network error' };
    }
  }, [cartId]);

  const applyPromoCode = useCallback(async (code) => {
    if (!cartId) return { success: false, error: 'No hay carrito' };
    
    try {
      const response = await fetch(`${API_URL}/api/cart/${cartId}/apply-promo?code=${encodeURIComponent(code)}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh cart to get updated totals
        await fetchCart(cartId);
        return { success: true, ...data };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Código inválido' };
      }
    } catch (err) {
      console.error('Error applying promo code:', err);
      return { success: false, error: 'Error al aplicar código' };
    }
  }, [cartId]);

  const removePromoCode = useCallback(async () => {
    if (!cartId) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/cart/${cartId}/remove-promo`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchCart(cartId);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error removing promo code:', err);
      return false;
    }
  }, [cartId]);

  const clearCart = useCallback(() => {
    localStorage.removeItem('cartId');
    setCartId(null);
    setCart(null);
  }, []);

  const associateUserToCart = useCallback(async (token) => {
    if (!cartId || !token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/cart/${cartId}/associate-user`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchCart(cartId);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error associating user to cart:', err);
      return false;
    }
  }, [cartId]);

  // Register the callback for auth context
  useEffect(() => {
    setOnLoginCallback(associateUserToCart);
    return () => setOnLoginCallback(null);
  }, [associateUserToCart]);

  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{
      cart,
      cartId,
      loading,
      error,
      itemCount,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      getShippingEstimates,
      selectShipping,
      createCheckoutSession,
      createCashCheckout,
      applyPromoCode,
      removePromoCode,
      clearCart,
      associateUserToCart,
      refreshCart: () => cartId && fetchCart(cartId)
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
